import type { FC, KeyboardEvent } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import LoseChangesModal from '../modals/lose-changes-modal/lose-changes-modal';
import Typography from '../typography/typography';
import { LeftArrow } from './left-arrow';

export interface IBackHeaderProps {
  title: string;
  onBack?: () => void;
  hasChanges?: boolean;
  isFixed?: boolean;
  isTitleFsMasked?: boolean;
  noMargin?: boolean;
  /** When `noMargin` is true, adds standard left padding to the back button so it aligns with page content. */
  contentPadding?: boolean;
  backButtonAriaLabel?: string;
}

export const BackHeader: FC<IBackHeaderProps> = ({
  title,
  onBack,
  hasChanges,
  isFixed = false,
  isTitleFsMasked = false,
  noMargin = false,
  contentPadding = false,
  backButtonAriaLabel,
}: IBackHeaderProps) => {
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const handleBack = useCallback(() => {
    if (hasChanges) {
      setShowModal(true);
      return;
    }
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  }, [onBack, navigate, hasChanges]);

  const handleConfirm = useCallback(() => {
    setShowModal(false);
    onBack ? onBack() : navigate(-1);
  }, [onBack, navigate]);

  const handleCancel = () => setShowModal(false);

  return (
    <Wrapper fixed={isFixed} noMargin={noMargin}>
      <Container
        onClick={handleBack}
        data-testid="back-header-container"
        role="button"
        tabIndex={0}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          const eventKey = event.key;
          if (eventKey === 'Enter' || eventKey === ' ') {
            handleBack();
          }
        }}
        aria-label={backButtonAriaLabel || `Go back to ${title}`}
        $contentPadding={contentPadding}
      >
        <LeftArrow />
        <Title
          variant={TypographyVariant.bodyMd2}
          color={theme.palette.primary.main}
          className={isTitleFsMasked ? 'fs-mask' : undefined}
        >
          {title}
        </Title>
      </Container>
      <LoseChangesModal
        isOpen={showModal}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />
    </Wrapper>
  );
};

export default BackHeader;

const Wrapper = styled.div<{
  fixed?: boolean;
  noMargin?: boolean;
}>`
  ${({ theme, fixed, noMargin }) => css`
    display: flex;
    width: calc(100% - ${noMargin ? 0 : theme.spacing(10)});
    border-bottom: 1px solid ${theme.palette.neutral[60]};
    background: ${theme.palette.neutral['00']};
    position: ${fixed ? 'sticky' : 'relative'};
    z-index: 150;
    top: 0px;
    margin: ${noMargin ? undefined : theme.spacing(0, 5)};

    @media ${theme.mediaQueries.xs},
    ${theme.mediaQueries.sm},
    ${theme.mediaQueries.md} {
      margin: ${noMargin ? undefined : theme.spacing(0, 2)};
      width: calc(100% - ${noMargin ? 0 : theme.spacing(4)});
      top:  0px;
      };
  }
  `}
`;

const Container = styled.div<{ $contentPadding?: boolean }>`
  ${({ theme, $contentPadding }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.spacing(1)};
    cursor: pointer;
    width: fit-content;
    margin: ${$contentPadding
      ? theme.spacing(3, 5, 2, 5)
      : theme.spacing(3, 5, 2, 0)};
  `}
`;

const Title = styled(Typography)`
  line-height: 16px;
`;
